import { ChevronRightIcon, EyeIcon, Link2Icon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ROLE } from '../../../../common/role';
import { unitTypeLabel, type WorkPackageItem } from '../../../../common/work-package';
import { usePermission } from '../../../../hooks/use-permission';
import { cn, formatCurrency } from '../../../../lib/utils';
import { DeleteButton } from '../../../DeleteButton';
import { Restricted } from '../../../Restricted';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { TableCell, TableRow } from '../../../ui/table';
import { NewLineItemRow } from './NewLineItemRow';

export type LineItemFormRowProps = {
	lineItems: Array<WorkPackageItem>;
	item: WorkPackageItem;
	newLineItems: Array<{ id?: string | null; parentItemId?: string | null; description?: string | null }>;
	expandedItems: Set<string>;
	onToggleItemExpansion: (itemId: string) => void;
	onAddChildItem: (parentItemId: string) => void;
	onDeleteItem: (itemId: string) => void;
	level?: number;
	onRemoveNewLineItem: (id: string) => void;
	onSaveNewLineItem: (data: { description: string; id: string }) => void;
	onAddNewLineItem: () => void;
	handleUpdateItem: (id: string, field: keyof WorkPackageItem, value: string | number) => void;
	count: number;
	parentItemNumber?: string;
	handleLinkDamageItems?: (id: string) => void;
};

export function LineItemRow({
	count,
	parentItemNumber,
	lineItems,
	item,
	onToggleItemExpansion,
	newLineItems,
	expandedItems,
	onAddChildItem,
	onDeleteItem,
	level = 0,
	onRemoveNewLineItem,
	onSaveNewLineItem,
	onAddNewLineItem,
	handleUpdateItem,
	handleLinkDamageItems,
}: LineItemFormRowProps) {
	const { t } = useTranslation();
	const children = lineItems.filter((i) => i.parentItemId === item.id);
	const newChildren = newLineItems.filter((n) => n.parentItemId === item.id);
	const hasChildren = children.length > 0 || newChildren.length > 0;
	const isExpanded = expandedItems.has(item.id as string);
	const itemNumber = parentItemNumber ? `${parentItemNumber}.${count}` : `${count}`;
	const viewDamage = () => {
		if (!item.damageId) return;
		if (!item.damage?.assetId || !item.damage?.inspectionId) return;

		window.open(
			`/assets/${item.damage?.assetId}/inspections/${item.damage?.inspectionId}/damages/${item.damageId}`,
			'_blank',
		);
	};
	const associateDamage = () => {
		if (!handleLinkDamageItems) return;
		handleLinkDamageItems(item.id as string);
	};
	const hasUpdatePermission = usePermission(ROLE.WORK_PACKAGES_UPDATE);
	return (
		<>
			<TableRow className="group">
				<TableCell className="text-muted-foreground">
					<div className="flex items-center gap-1">
						{hasChildren ? (
							<Button
								type="button"
								variant="ghost"
								size="icon-sm"
								onClick={() => onToggleItemExpansion(item.id as string)}
								className="size-4"
								aria-label={isExpanded ? 'Collapse' : 'Expand'}
								aria-expanded={isExpanded}
							>
								<ChevronRightIcon
									className={cn('text-muted-foreground transition-transform', isExpanded && 'rotate-90')}
								/>
							</Button>
						) : (
							<div className="w-4" />
						)}
						<span>{itemNumber}</span>
						<Restricted to={ROLE.WORK_PACKAGES_UPDATE}>
							<Button
								variant="default"
								size="sm"
								className="h-6 rounded ml-1 opacity-0 group-hover:opacity-100 flex items-center gap-0.5"
								onClick={() => onAddChildItem(item.id as string)}
								title={t('finance.items.addChildItem')}
							>
								<span className="text-xs font-bold leading-none mb-0.5">↳</span>
								<PlusIcon className="size-3" />
							</Button>
						</Restricted>
					</div>
				</TableCell>
				<TableCell>
					<div className="flex items-center gap-2">
						<div className="flex-1">
							<Input
								value={item.description || ''}
								onChange={(e) => handleUpdateItem(item.id as string, 'description', e.target.value)}
								placeholder={t('finance.items.enterDescription')}
								className="h-8 bg-muted"
								disabled={!hasUpdatePermission}
							/>
							{item.damageId && <div className="mt-1 text-xs text-muted-foreground font-mono">{item.damageId}</div>}
						</div>
						{!item.damageId ? (
							<Button
								size="sm"
								variant="ghost"
								className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 shrink-0"
								title={t('finance.items.associateDamage')}
								onClick={associateDamage}
								disabled={!hasUpdatePermission}
							>
								<Link2Icon className="h-4 w-4" />
							</Button>
						) : (
							<Button
								size="sm"
								variant="ghost"
								className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 shrink-0"
								title={t('finance.items.viewDamage')}
								onClick={viewDamage}
							>
								<EyeIcon className="h-4 w-4" />
							</Button>
						)}
					</div>
				</TableCell>
				<TableCell>
					<Input
						type="number"
						value={String(item.quantity)}
						onChange={(e) =>
							handleUpdateItem(item.id as string, 'quantity', e.target.value ? parseFloat(e.target.value) : '')
						}
						className="bg-input h-8 w-20"
						disabled={!hasUpdatePermission}
					/>
				</TableCell>
				<TableCell>
					<Select
						value={item.unitType || ''}
						onValueChange={(v) => handleUpdateItem(item.id as string, 'unitType', v)}
						disabled={!hasUpdatePermission}
					>
						<SelectTrigger className="bg-input h-8 w-20">
							<SelectValue />
						</SelectTrigger>
						<SelectContent position="popper">
							<SelectItem value="cm">cm</SelectItem>
							<SelectItem value="m²">m²</SelectItem>
							<SelectItem value="m³">m³</SelectItem>
							<SelectItem value="pcs">{unitTypeLabel(t, 'pcs')}</SelectItem>
						</SelectContent>
					</Select>
				</TableCell>
				<TableCell>
					<Input
						type="number"
						value={String(item.unitPrice)}
						onChange={(e) =>
							handleUpdateItem(item.id as string, 'unitPrice', e.target.value ? parseFloat(e.target.value) : '')
						}
						className="bg-input h-8 w-20"
						disabled={!hasUpdatePermission}
					/>
				</TableCell>
				<TableCell className="font-medium">
					{formatCurrency((item.quantity || 0) * Number(item.unitPrice || 0))}
				</TableCell>
				<TableCell>
					<Restricted to={ROLE.WORK_PACKAGES_UPDATE}>
						<DeleteButton
							iconOnly
							label={t('finance.items.deleteItem')}
							onClick={() => onDeleteItem(item.id as string)}
						/>
					</Restricted>
				</TableCell>
			</TableRow>
			{isExpanded && (
				<>
					{children.map((child, index) => (
						<LineItemRow
							count={index + 1}
							parentItemNumber={itemNumber}
							key={child.id}
							item={child}
							level={level + 1}
							onSaveNewLineItem={onSaveNewLineItem}
							onRemoveNewLineItem={onRemoveNewLineItem}
							lineItems={lineItems}
							newLineItems={newLineItems}
							expandedItems={expandedItems}
							onToggleItemExpansion={onToggleItemExpansion}
							onAddChildItem={onAddChildItem}
							onDeleteItem={onDeleteItem}
							onAddNewLineItem={onAddNewLineItem}
							handleUpdateItem={handleUpdateItem}
							handleLinkDamageItems={handleLinkDamageItems}
						/>
					))}
					{newChildren.map((newChild) => (
						<NewLineItemRow
							key={newChild.id}
							lineItem={newChild}
							level={level + 1}
							onSave={onSaveNewLineItem}
							onRemove={onRemoveNewLineItem}
						/>
					))}
				</>
			)}
		</>
	);
}
